import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  CreateProjectPayload,
  Project,
  UpdateProjectPayload,
} from "../types/api";
import * as projectService from "../services/projectService";
import { useToast } from "../components/ui/use-toast";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DataTable, type DataTableColumn } from "../components/table/DataTable";
import ProjectFormModal from "../components/admin/ProjectFormModal";
import { resolveAssetUrl } from "../utils/url";
import { useTranslation } from "react-i18next";
import { cn } from "../lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const PAGE_SIZE = 10;

const AdminProjectsPage = () => {
  const { toast } = useToast();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [saving, setSaving] = useState(false);
  const { t } = useTranslation();
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [progressProject, setProgressProject] = useState<Project | null>(null);
  const [progressAmount, setProgressAmount] = useState<string>("");
  const [progressSaving, setProgressSaving] = useState(false);

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const data = await projectService.listProjects();
      setProjects(data);
    } catch (error) {
      toast({
        title: "Erreur",
        description:
          typeof error === "string"
            ? error
            : error instanceof Error
            ? error.message
            : "Impossible de charger les projets.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void fetchProjects();
  }, [fetchProjects]);

  const filteredProjects = useMemo(() => {
    // La recherche reste basée sur le nom FR + symbol + catégorie FR
    const term = search.trim().toLowerCase();
    if (!term) {
      return projects;
    }
    return projects.filter((project) => {
      return (
        project.name.toLowerCase().includes(term) ||
        project.symbol.toLowerCase().includes(term) ||
        project.category.toLowerCase().includes(term)
      );
    });
  }, [projects, search]);

  // Réinitialiser la page quand la recherche change
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  // Pagination des projets filtrés
  const paginatedProjects = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    return filteredProjects.slice(startIndex, endIndex);
  }, [filteredProjects, currentPage]);

  const totalPages = Math.max(1, Math.ceil(filteredProjects.length / PAGE_SIZE));

  // Réinitialiser la page si nécessaire
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  const handleOpenCreate = () => {
    setSelectedProject(null);
    setModalOpen(true);
  };

  const handleEdit = useCallback(async (project: Project) => {
    try {
      // Recharger le projet complet avec toutes ses images (galerie incluse)
      const fullProject = await projectService.getProjectById(project.id);
      setSelectedProject(fullProject);
      setModalOpen(true);
    } catch (error) {
      toast({
        title: "Erreur",
        description:
          typeof error === "string"
            ? error
            : error instanceof Error
            ? error.message
            : "Impossible de charger les détails du projet.",
        variant: "destructive",
      });
    }
  }, [toast]);

  const handleDelete = useCallback(
    async (project: Project) => {
      const ok = window.confirm(
        `Supprimer le projet « ${project.name} » ? Cette opération est définitive.`
      );
      if (!ok) {
        return;
      }
      try {
        setDeletingId(project.id);
        await projectService.deleteProject(project.id);
        toast({
          title: t("admin.projectDeleteSuccessTitle", "Projet supprimé"),
          description: t("admin.projectDeleteSuccessDescription", {
            name: project.name,
          }),
          variant: "success",
        });
        await fetchProjects();
      } catch (error) {
        toast({
          title: t("admin.dashboardCreateErrorTitle"),
          description:
            typeof error === "string"
              ? error
              : error instanceof Error
              ? error.message
              : t(
                  "admin.projectDeleteErrorDescription",
                  "Impossible de supprimer le projet."
                ),
          variant: "destructive",
        });
      } finally {
        setDeletingId(null);
      }
    },
    [fetchProjects, toast]
  );

  const handleSubmit = useCallback(
    async (
      payload: CreateProjectPayload | UpdateProjectPayload,
      isEdit: boolean
    ) => {
      try {
        setSaving(true);
        if (isEdit && selectedProject) {
          await projectService.updateProject(
            selectedProject.id,
            payload as UpdateProjectPayload
          );
          toast({
            title: t("admin.projectUpdateSuccessTitle", "Projet mis à jour"),
            description: t("admin.projectUpdateSuccessDescription", {
              name: selectedProject.name,
            }),
            variant: "success",
          });
        } else {
          await projectService.createProject(payload as CreateProjectPayload);
          toast({
            title: t("admin.projectCreateSuccessTitle", "Projet créé"),
            description: t(
              "admin.projectCreateSuccessDescription",
              "Le projet est désormais visible côté clients."
            ),
            variant: "success",
          });
        }
        await fetchProjects();
        setModalOpen(false);
        setSelectedProject(null);
      } catch (error) {
        toast({
          title: t("admin.dashboardCreateErrorTitle"),
          description:
            typeof error === "string"
              ? error
              : error instanceof Error
              ? error.message
              : t(
                  "admin.projectSaveErrorDescription",
                  "Impossible d’enregistrer le projet."
                ),
          variant: "destructive",
        });
        throw error;
      } finally {
        setSaving(false);
      }
    },
    [fetchProjects, selectedProject, toast]
  );

  const columns = useMemo<DataTableColumn<Project>[]>(
    () => [
      {
        key: "name",
        header: "Projet",
        cell: (project) => (
          <div className="flex items-center gap-3">
            {project.imageUrl && (
              <img
                src={resolveAssetUrl(project.imageUrl) ?? undefined}
                alt={project.name}
                className="h-14 w-14 flex-shrink-0 rounded-xl border border-border object-cover dark:border-white/10"
                crossOrigin="anonymous"
                onError={(e) => {
                  console.error(
                    "Erreur de chargement de l'image:",
                    project.imageUrl,
                    e
                  );
                }}
              />
            )}
            <div className="min-w-0 flex-1">
              <p className="mb-1 text-base font-semibold leading-tight text-foreground dark:text-white">
                {project.name}
              </p>
              <p className="font-mono text-xs text-muted-foreground dark:text-white/50">
                {project.symbol}
              </p>
            </div>
          </div>
        ),
      },
      {
        key: "category",
        header: "Catégorie",
        cell: (project) => (
          <span className="text-sm font-medium text-muted-foreground dark:text-white/70">
            {project.category}
          </span>
        ),
        hideOnMobile: true,
      },
      {
        key: "status",
        header: "Statut",
        cell: (project) => {
          const statusColors = {
            OPEN:
              "bg-emerald-100 text-emerald-900 border-emerald-200 dark:bg-emerald-500/20 dark:text-emerald-300 dark:border-emerald-500/30",
            UPCOMING:
              "bg-blue-100 text-blue-900 border-blue-200 dark:bg-blue-500/20 dark:text-blue-300 dark:border-blue-500/30",
            CLOSED:
              "bg-slate-200 text-slate-800 border-slate-300 dark:bg-gray-500/20 dark:text-gray-300 dark:border-gray-500/30",
          };
          const colorClass =
            statusColors[project.status as keyof typeof statusColors] ||
            "bg-muted text-muted-foreground border-border dark:bg-white/10 dark:text-white/70 dark:border-white/15";
          return (
            <span className={cn(
              "inline-flex rounded-full border px-3 py-1 text-xs font-medium uppercase tracking-wide",
              colorClass
            )}>
              {project.status}
            </span>
          );
        },
      },
      {
        key: "metrics",
        header: "Objectif",
        cell: (project) => {
          const progressPercent =
            project.goal > 0
              ? Math.min(
                  Math.max(0, (project.totalRaised / project.goal) * 100),
                  100
                )
              : 0;

          return (
            <div className="space-y-1">
              <div className="flex items-baseline gap-2">
                <p className="text-lg font-bold text-foreground dark:text-white">
                  {project.goal.toLocaleString()} $
                </p>
              </div>
              <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground dark:text-white/60">
                <span className="flex items-center gap-1">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-600 dark:bg-emerald-400" />
                  {project.activeContributors} contributeurs actifs
                </span>
                <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                  {progressPercent.toFixed(2)}%
                </span>
              </div>
            </div>
          );
        },
      },
      {
        key: "actions",
        header: "Actions",
        cell: (project) => (
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setProgressProject(project);
                setProgressAmount("");
              }}
              className="text-xs border-emerald-200 bg-emerald-50 text-emerald-900 hover:bg-emerald-100 dark:border-white/20 dark:bg-emerald-500/10 dark:text-emerald-300 dark:hover:bg-emerald-500/20"
            >
              {t("admin.projectAddProgressButton", "Ajouter progression")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleEdit(project)}
              className="text-xs border-border hover:bg-muted dark:border-white/20 dark:hover:bg-white/10">
              Modifier
            </Button>
            <Button
              size="sm"
              variant="destructive"
              disabled={deletingId === project.id}
              onClick={() => handleDelete(project)}
              className="text-xs">
              {deletingId === project.id ? "Suppression..." : "Supprimer"}
            </Button>
          </div>
        ),
      },
    ],
    [deletingId, handleDelete, handleEdit]
  );

  return (
    <section className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground dark:text-white">
            {t("admin.projectsPageTitle")}
          </h2>
          <p className="text-sm text-muted-foreground dark:text-white/70">
            {t("admin.projectsPageSubtitle")}
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Input
            placeholder={t("admin.projectsSearchPlaceholder")}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="bg-background text-foreground border border-input dark:bg-white/5 dark:text-white"
          />
          <Button onClick={handleOpenCreate}>
            {t("admin.projectsNewButton")}
          </Button>
        </div>
      </div>

      <DataTable
        data={paginatedProjects}
        columns={columns}
        rowKey={(project) => project.id}
        isLoading={loading}
        emptyState={t("admin.projectsEmpty")}
        pagination={{
          currentPage,
          totalPages,
          totalItems: filteredProjects.length,
          itemsPerPage: PAGE_SIZE,
          onPageChange: setCurrentPage,
          isLoading: loading,
        }}
      />

      {/* Dialog pour ajouter un montant de progression à un projet */}
      <Dialog
        open={Boolean(progressProject)}
        onOpenChange={(open) => {
          if (!open) {
            setProgressProject(null);
            setProgressAmount("");
            setProgressSaving(false);
          }
        }}
      >
        <DialogContent className="max-w-md border border-border bg-card text-foreground dark:border-white/10 dark:bg-slate-950 dark:text-white">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-foreground dark:text-white">
              {t(
                "admin.projectAddProgressButton",
                "Ajouter progression"
              )}{" "}
              {progressProject ? `- ${progressProject.name}` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-white/70">
              {t(
                "admin.projectAddProgressPrompt",
                "Montant à ajouter à la progression (en USD)"
              )}
            </p>
            <Input
              type="number"
              min={0}
              step="0.01"
              value={progressAmount}
              onChange={(event) => setProgressAmount(event.target.value)}
              className="border border-input bg-background text-foreground dark:bg-white/5 dark:text-white dark:border-white/20"
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                disabled={progressSaving}
                onClick={() => {
                  setProgressProject(null);
                  setProgressAmount("");
                  setProgressSaving(false);
                }}
                className="border-border text-foreground hover:bg-muted dark:border-white/30 dark:text-white/80 dark:hover:bg-white/10"
              >
                {t("common.cancel", "Annuler")}
              </Button>
              <Button
                type="button"
                disabled={progressSaving || !progressProject}
                onClick={async () => {
                  if (!progressProject) return;
                  const normalized = progressAmount.replace(",", ".").trim();
                  const amount = Number(normalized);
                  if (!Number.isFinite(amount) || amount <= 0) {
                    toast({
                      title: t(
                        "admin.projectAddProgressInvalidTitle",
                        "Montant invalide"
                      ),
                      description: t(
                        "admin.projectAddProgressInvalidDescription",
                        "Veuillez saisir un montant numérique strictement positif."
                      ),
                      variant: "destructive",
                    });
                    return;
                  }
                  try {
                    setProgressSaving(true);
                    await projectService.updateProject(progressProject.id, {
                      progressSeedDelta: amount,
                    });
                    await fetchProjects();
                    toast({
                      title: t(
                        "admin.projectAddProgressSuccessTitle",
                        "Progression mise à jour"
                      ),
                      description: t(
                        "admin.projectAddProgressSuccessDescription",
                        "La progression de l'objectif du projet a été mise à jour."
                      ),
                      variant: "success",
                    });
                    setProgressProject(null);
                    setProgressAmount("");
                    setProgressSaving(false);
                  } catch (error: any) {
                    const message =
                      typeof error === "string"
                        ? error
                        : error?.message ??
                          t(
                            "admin.projectAddProgressErrorDescription",
                            "Impossible de mettre à jour la progression."
                          );
                    toast({
                      title: t(
                        "admin.projectAddProgressErrorTitle",
                        "Erreur de mise à jour"
                      ),
                      description: message,
                      variant: "destructive",
                    });
                    setProgressSaving(false);
                  }
                }}
                className="bg-emerald-500 text-slate-950 hover:bg-emerald-400"
              >
                {progressSaving
                  ? t("admin.saving", "Enregistrement...")
                  : t("admin.save", "Enregistrer")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ProjectFormModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedProject(null);
        }}
        onSubmit={handleSubmit}
        isSubmitting={saving}
        project={selectedProject}
      />
    </section>
  );
};

export default AdminProjectsPage;
